/* eslint-disable no-redeclare */
/* eslint-disable @typescript-eslint/no-namespace */

export type RicosDocument = {
  nodes: RootNode[];
  /** Information about the Ricos document itself, such as its version. Only relevant when building the Ricos document using the [Ricos playground](https:/ricos.dev), in which case it is auto-generated. */
  metadata?: Metadata;
  /** Includes styling to be applied throughout the Ricos document for the specified node types. For example, you can define general styling for `headerOne`, and then override some of the properties separately for a specific header. */
  documentStyle?: DocumentStyle;
};
export type NodeStyle = {
  /** The top padding value in pixels. */
  paddingTop?: string;
  /** The bottom padding value in pixels. */
  paddingBottom?: string;
  backgroundColor?: string;
};
/**
 * Sets a default styling for specific node types throughout the entire Ricos Document. For example, you can set the text color for all Heading 1 nodes.
 * You can override the styling of specific Text nodes separately using [decorations](#decorations).
 */
export type DocumentStyle = {
  /** Style for the H1 nodes. */
  headerOne?: TextNodeStyle;
  /** Style for the H2 nodes. */
  headerTwo?: TextNodeStyle;
  /** Style for the H3 nodes. */
  headerThree?: TextNodeStyle;
  /** Style for the H4 nodes. */
  headerFour?: TextNodeStyle;
  /** Style for the H5 nodes. */
  headerFive?: TextNodeStyle;
  /** Style for the H6 nodes. */
  headerSix?: TextNodeStyle;
  /** Style for the paragraph nodes. */
  paragraph?: TextNodeStyle;
  /** Style for the block quote nodes. */
  blockquote?: TextNodeStyle;
  /** Style for the codeblock nodes. */
  codeBlock?: TextNodeStyle;
};
export type TextNodeStyle = {
  decorations?: Decoration[];
  /**
   * Node style, such as padding.
   *  **Note:** This node requires the [lineSpacing](#plugins) plugin to be enabled.
   */
  nodeStyle?: NodeStyle;
  /** Text line height. */
  lineHeight?: string;
};
export type Metadata = {
  /** Ricos document version. */
  version?: number;
  id?: any;
  createdTimestamp?: any;
  updatedTimestamp?: any;
};
export type TextStyle = {
  /** Text alignment. Supported values: <br> - `AUTO`: Text aligns itself automatically. <br> - `LEFT`: Text is aligned to the left. <br> - `RIGHT`: Text is aligned to the right. <br> - `CENTER`: Text is centered. <br> - `JUSTIFY`: Text is justified to both margins. <br> Default: `AUTO`. */
  textAlignment?: TextStyle_TextAlignment;
  /** Vertical spacing between lines of text in an element. */
  lineHeight?: string;
};
export type TextStyle_TextAlignment = 'AUTO' | 'LEFT' | 'RIGHT' | 'CENTER' | 'JUSTIFY';

export const TextStyle_TextAlignment = {
  AUTO: 'AUTO',
  LEFT: 'LEFT',
  RIGHT: 'RIGHT',
  CENTER: 'CENTER',
  JUSTIFY: 'JUSTIFY',
} as const;

/** Link object. */
export type Link = {
  /** Absolute URL to link to. */
  url?: string;
  /** Target [Node ID](#node-ids) to link to. */
  anchor?: string;
  /** The HTML `target` attribute value for the link. This property defines where the link opens. <br> Supported values: <br> `SELF` - Opens the link in the same frame.<br> `BLANK` - Opens the link in a new browser tab or window.<br> `PARENT` - Opens the link in the parent frame.<br> `TOP` - Opens the link in the full body of the link's browser tab or window. <br> Default: `SELF` */
  target?: Link_Target;
  /** Relationship between the current document and the linked document. */
  rel?: Link_Rel;
  /** A serialized object used for a custom or external link panel. */
  customData?: string;
};
export type Link_Target = 'SELF' | 'BLANK' | 'PARENT' | 'TOP';

export const Link_Target = {
  SELF: 'SELF',
  BLANK: 'BLANK',
  PARENT: 'PARENT',
  TOP: 'TOP',
} as const;

export type Link_Rel = {
  /**
   * Indicates to search engine crawlers not to follow the link.
   *  Default: `false`
   */
  nofollow?: boolean;
  /**
   * Indicates to search engine crawlers that the link is a paid placement such as sponsored content or an advertisement.
   *  Default: `false`
   */
  sponsored?: boolean;
  /**
   * Indicates that this link is user-generated content and isn't necessarily trusted or endorsed by the page author.
   *  Default: `false`
   */
  ugc?: boolean;
  /** Indicates that this link protects referral information from being passed to the target website. */
  noreferrer?: boolean;
};
/** File source object. */
export type FileSource = {
  /** File source URL. */
  url?: string;
  /** ID that's resolved to a URL by a resolver function. */
  id?: string;
  /**
   * Whether the file's source is private. Learn more about [private files](https://dev.wix.com/docs/rest/assets/media/media-manager/files/private-files) in Wix.
   *  Default: `false`
   */
  private?: boolean;
};
export type Media = {
  /** File source. */
  src: FileSource;
  /** Media width in pixels. */
  width?: number;
  /** Media height in pixels. */
  height?: number;
  /** Media duration in seconds. Only relevant for audio and video files. */
  duration?: number;
};
export type Never = {
  _neverCreatedElement: any;
};
export type VerticalAlignment = 'TOP' | 'MIDDLE' | 'BOTTOM';

export const VerticalAlignment = {
  TOP: 'TOP',
  MIDDLE: 'MIDDLE',
  BOTTOM: 'BOTTOM',
} as const;

export type BackgroundImage = {
  /** Background image. */
  media?: Media;
  /** Background image opacity. */
  opacity?: number;
  /** Background image scaling. */
  scaling?: ImageScaling;
  /** Position of background. Defaults to `CENTER`. */
  position?: ImagePosition;
};
export type ImageScaling = 'AUTO' | 'CONTAIN' | 'COVER';

export const ImageScaling = {
  AUTO: 'AUTO',
  CONTAIN: 'CONTAIN',
  COVER: 'COVER',
} as const;

export type ImagePosition =
  | 'CENTER'
  | 'CENTER_LEFT'
  | 'CENTER_RIGHT'
  | 'TOP'
  | 'TOP_LEFT'
  | 'TOP_RIGHT'
  | 'BOTTOM'
  | 'BOTTOM_LEFT'
  | 'BOTTOM_RIGHT';

export const ImagePosition = {
  CENTER: 'CENTER',
  CENTER_LEFT: 'CENTER_LEFT',
  CENTER_RIGHT: 'CENTER_RIGHT',
  TOP: 'TOP',
  TOP_LEFT: 'TOP_LEFT',
  TOP_RIGHT: 'TOP_RIGHT',
  BOTTOM: 'BOTTOM',
  BOTTOM_LEFT: 'BOTTOM_LEFT',
  BOTTOM_RIGHT: 'BOTTOM_RIGHT',
} as const;

export type PluginContainerData = {
  /** Node display width. */
  width?: PluginContainerData_Width;
  /** Node alignment in its container. */
  alignment?: PluginContainerData_Alignment;
  /** Spoiler cover settings. */
  spoiler?: PluginContainerData_Spoiler;
  /** Node display height. */
  height?: PluginContainerData_Height;
  /**
   * Whether the text should wrap around the node when it's displayed. If `textWrap` is `false`, the node fills the width of its container.
   *  Default: `true` for all node types except `DIVIDER`.
   */
  textWrap?: boolean;
};
export type PluginContainerData_Alignment = 'CENTER' | 'LEFT' | 'RIGHT';

export const PluginContainerData_Alignment = {
  CENTER: 'CENTER',
  LEFT: 'LEFT',
  RIGHT: 'RIGHT',
} as const;

export type PluginContainerData_Spoiler = {
  /**
   * Whether the spoiler cover is enabled.
   *  Default: `false`
   */
  enabled?: boolean;
  /** Text displayed on the spoiler cover. */
  description?: string;
  /** Text that appears on the button that removes the spoiler cover. */
  buttonText?: string;
};
/** Node display width. */
export type PluginContainerData_Width = {
  /**
   * Width of a node in a container. Supported values:
   *  - `CONTENT`: Width of the container matches the content width.
   *  - `SMALL`: Small width.
   *  - `ORIGINAL`: Width of the container matches the original image width. **Note:** This value is only applicable for `imageData` containers.
   *  `FULL_WIDTH`: Image container takes up the full width of the screen. **Note:** This value is only applicable for `imageData` containers.
   */
  size?: PluginContainerData_Width_Type;
  /** A custom width value in pixels. */
  custom?: string;
};
export type PluginContainerData_Width_Type = 'CONTENT' | 'SMALL' | 'ORIGINAL' | 'FULL_WIDTH';

export const PluginContainerData_Width_Type = {
  CONTENT: 'CONTENT',
  SMALL: 'SMALL',
  ORIGINAL: 'ORIGINAL',
  FULL_WIDTH: 'FULL_WIDTH',
} as const;

export type PluginContainerData_Height = {
  /** Node display height in pixels. */
  custom?: string;
};
export type Decoration =
  | BoldDecoration
  | ItalicDecoration
  | UnderlineDecoration
  | SpoilerDecoration
  | AnchorDecoration
  | MentionDecoration
  | LinkDecoration
  | ColorDecoration
  | FontSizeDecoration
  | StrikethroughDecoration
  | SuperscriptDecoration
  | SubscriptDecoration;
export type Decoration_Type =
  | 'BOLD'
  | 'ITALIC'
  | 'UNDERLINE'
  | 'SPOILER'
  | 'ANCHOR'
  | 'MENTION'
  | 'LINK'
  | 'COLOR'
  | 'FONT_SIZE'
  | 'STRIKETHROUGH'
  | 'SUPERSCRIPT'
  | 'SUBSCRIPT';

export const Decoration_Type = {
  BOLD: 'BOLD',
  ITALIC: 'ITALIC',
  UNDERLINE: 'UNDERLINE',
  SPOILER: 'SPOILER',
  ANCHOR: 'ANCHOR',
  MENTION: 'MENTION',
  LINK: 'LINK',
  COLOR: 'COLOR',
  FONT_SIZE: 'FONT_SIZE',
  STRIKETHROUGH: 'STRIKETHROUGH',
  SUPERSCRIPT: 'SUPERSCRIPT',
  SUBSCRIPT: 'SUBSCRIPT',
} as const;

/** Bolds text, which makes it appear thicker and more prominent than normal text. */
export type BoldDecoration = {
  /**
   * Decoration type.
   *  Must be `BOLD`.
   */
  type: 'BOLD';
  /** Font weight. */
  fontWeightValue?: number;
};
/** Italicizes text, which makes it appear slanted or italicized. Italics are typically used for emphasis, citations, or to distinguish certain types of content. */
export type ItalicDecoration = {
  /**
   * Decoration type.
   *  Must be `ITALIC`.
   */
  type: 'ITALIC';
  /** Whether to enable the italic text. */
  italicData?: boolean;
};
/** Underlines text. */
export type UnderlineDecoration = {
  /**
   * Decoration type.
   * Must be `UNDERLINE`.
   */
  type: 'UNDERLINE';
  /** Whether to underline the text. */
  underlineData?: boolean;
};
/** Strikes-through text. */
export type StrikethroughDecoration = {
  /**
   * Decoration type.
   * Must be `STRIKETHROUGH`.
   */
  type: 'STRIKETHROUGH';
  /** Whether to strikethrough the text. Default: `true`. */
  strikethroughData?: boolean;
};
/** Makes text appear smaller and raised above the baseline. */
export type SuperscriptDecoration = {
  /**
   * Decoration type.
   *  Must be `SUPERSCRIPT`.
   */
  type: 'SUPERSCRIPT';
  /** Whether to make the text appear smaller and raised above the baseline. */
  superscriptData?: boolean;
};
/** Makes text appear smaller and lowered below the baseline. */
export type SubscriptDecoration = {
  /**
   * Decoration type.
   *  Must be `SUBSCRIPT`.
   */
  type: 'SUBSCRIPT';
  /** Whether to make the text appear smaller and lowered below the baseline. */
  subscriptData?: boolean;
};
/** Blurs text. <br> **Note:** This decoration requires the [`spoiler`](https://dev.wix.com/docs/ricos/getting-started/introduction#plugins) plugin to be enabled. */
export type SpoilerDecoration = {
  /**
   * Decoration type.
   *  Must be `SPOILER`.
   */
  type: 'SPOILER';
  /** Spoiler details. */
  spoilerData?: SpoilerData;
};
/** Makes clickable text a different color. The link navigates to another section of the same page. */
export type AnchorDecoration = {
  /**
   * Decoration type.
   *  Must be `ANCHOR`.
   */
  type: 'ANCHOR';
  /** Anchor details. */
  anchorData?: AnchorData;
};
/** Text that mentions a specific user, often in social media platforms, forums, or content management systems. <br> **Note:** This decoration requires the [`mention`](https://dev.wix.com/docs/ricos/getting-started/introduction#plugins) plugin to be enabled. */
export type MentionDecoration = {
  /**
   * Decoration type.
   *  Must be `MENTION`.
   */
  type: 'MENTION';
  /** Anchor details. */
  mentionData?: MentionData;
};
/** Makes clickable text a different color. The link navigates to another webpage or resource. <br> **Note:** This decoration requires the [`link`](https://dev.wix.com/docs/ricos/getting-started/introduction#plugins) plugin to be enabled. */
export type LinkDecoration = {
  /**
   * Decoration type.
   *  Must be `LINK`.
   */
  type: 'LINK';
  /** Link details. */
  linkData?: LinkData;
};
/** Styles text using background and text colors. <br> **Note:** This decoration requires the [`colorDecoration`](https://dev.wix.com/docs/ricos/getting-started/introduction#plugins) plugin to be enabled. */
export type ColorDecoration = {
  /**
   * Decoration type.
   *  Value: `COLOR`
   */
  type: 'COLOR';
  /** Color details. */
  colorData?: ColorData;
};
/** Text font size. */
export type FontSizeDecoration = {
  /**
   * Decoration type.
   *  Must be `FONT_SIZE`.
   */
  type: 'FONT_SIZE';
  /** Font size details. */
  fontSizeData?: FontSizeData;
};
export type AnchorData = {
  /** Target Node ID to redirect to. */
  anchor?: string;
};
export type ColorData = {
  /** Background color as a hexadecimal value. */
  background?: string;
  /** Text color as a hexadecimal value. */
  foreground?: string;
};
export type LinkData = {
  /** Link object. */
  link?: Link;
};
export type MentionData = {
  /** Display name of the mentioned user. */
  name?: string;
  /** Username of the mentioned user. This is th text that appears after the "@". */
  slug?: string;
  /** ID of the mentioned user. */
  id?: string;
};
export type FontSizeData = {
  /** Font size value. */
  value: number;
  /**
   * Font size units.
   *  Default: `PX`
   */
  unit?: FontSizeData_fontType;
};
export type FontSizeData_fontType = 'PX' | 'EM';

export const FontSizeData_fontType = {
  PX: 'PX',
  EM: 'EM',
} as const;

export type SpoilerData = {
  /** [Node ID](#node-ids). */
  id?: string;
};
/** Used to visually distinguish quoted or cited content from the surrounding text. When rendered, it displays as the quoted text with indentation or other styling to visually separate it from the surrounding content. */
export type BlockquoteNode = {
  /**
   * Node type.
   *  Must be `BLOCKQUOTE`.
   */
  type: 'BLOCKQUOTE';
  nodes: ParagraphNode[];
  /** [Node ID](#node-ids). */
  id?: string;
  /** Blockquote details. */
  blockquoteData?: BlockquoteData;
  /**
   * Defines the blockquote style, such as padding and background color.
   *  **Note:** This node requires the [`lineSpacing`](https://dev.wix.com/docs/ricos/getting-started/introduction#plugins) plugin to be enabled.
   */
  style?: NodeStyle;
};
export type BlockquoteData = {
  /**
   * Blockquote margin from the left side.
   *  The indentation is measured relative to the font size *em* units by a ratio of 1.5. For example, if the indentation is `2`, then the margin is equal to **3em**. Therefore the margin is 3 times the size of the font.
   */
  indentation?: number;
};
/** Caption container to be attached to media elements, such as images. Captions support formatted text content and can be styled independently from their parent nodes. */
export type CaptionNode = {
  /**
   * Node type.
   *  Must be `CAPTION`.
   */
  type: 'CAPTION';
  nodes: TextNode[];
  /** Caption [node ID](#node-ids). */
  id?: string;
  /**
   * Defines the caption styling, such as padding and background color.
   *  **Note:** This node requires the [`lineSpacing`](https://dev.wix.com/docs/ricos/getting-started/introduction#plugins) plugin to be enabled.
   */
  style?: NodeStyle;
  /** Caption options. */
  captionData?: CaptionData;
};
export type CaptionData = {
  /** Caption text styling. */
  textStyle?: TextStyle;
};
/** Presents code snippets, markup, or other text-based content that should be displayed exactly as it appears, without any interpretation or formatting applied by the browser. When rendered, it is displayed as the code block with a monospaced font, preserving indentation, line breaks, and spacing as specified in the code. */
export type CodeBlockNode = {
  /**
   * Node type.
   *  VMust be `CODE_BLOCK`.
   */
  type: 'CODE_BLOCK';
  nodes?: TextNode[];
  /** [Node ID](#node-ids). */
  id?: string;
  /** Code block details. */
  codeBlockData?: CodeBlockData;
  /**
   * Code block styling, such as padding and background color.
   *  **Note:** This node requires the [`lineSpacing`](https://dev.wix.com/docs/ricos/getting-started/introduction#plugins) plugin to be enabled.
   */
  style?: NodeStyle;
};
export type CodeBlockData = {
  /** Defines the text styling, such as alignment and line height. */
  textStyle?: TextStyle;
};
/** Indicates a heading or title of a section. There are 6 levels of headings, with `1` being the most prominent and `6` being the least. */
export type HeadingNode = {
  /**
   * Node type.
   *  Must be `HEADING`.
   */
  type: 'HEADING';
  nodes?: TextNode[];
  /** [Node ID](#node-ids). */
  id?: string;
  /**
   * Heading styling, such as padding and background color.
   *  **Note:** This node requires the [`lineSpacing`](https://dev.wix.com/docs/ricos/getting-started/introduction#plugins) plugin to be enabled.
   */
  style?: NodeStyle;
  /** Heading options. */
  headingData?: HeadingData;
};
export type HeadingData = {
  /** Heading size. <br> Mi: `1` <br> Max: `6` */
  level?: number;
  /** Heading text styling, such as text alignment and line height. */
  textStyle?: TextStyle;
  /**
   * Heading margin from the left side.
   *  The indentation is measured relative to the font size *em* units by a ratio of 1.5. For example, if the indentation is `2`, then the margin is equal to **3em**. Therefore the margin is 3 times the size of the font.
   */
  indentation?: number;
};
/** Used to wrap cells in layout container. When rendered, it cells one after the other in a row */
export type LayoutNode = {
  /**
   * Node type.
   *  Must be `LAYOUT`.
   */
  type: 'LAYOUT';
  nodes: LayoutCellNode[];
  /** [Node ID](#node-ids). */
  id?: string;
  /** Layout details. */
  layoutData?: LayoutData;
};
export type LayoutData = {
  /** Background color as a hexadecimal value. */
  backgroundColor?: string;
  /** Background image. */
  backgroundImage?: BackgroundImage;
  /** Border color as a hexadecimal value. */
  borderColor?: string;
  /** Border width in pixels. */
  borderWidth?: number;
  /** Border radius in pixels. */
  borderRadius?: number;
  /** Background color as a hexadecimal value. */
  backdropColor?: string;
  /** Background image. */
  backdropImage?: BackgroundImage;
  /** Backdrop padding top in pixels. */
  backdropPaddingTop?: number;
  /** Backdrop padding bottom in pixels. */
  backdropPaddingBottom?: number;
  /** Horizontal and vertical gap between columns in pixels. */
  gap?: number;
  /** Responsiveness behaviour. */
  responsivenessBehaviour?: LayoutData_ResponsivenessBehaviour;
  /** Size in pixels when responsivenessBehaviour applies. */
  responsivenessBreakpoint?: number;
  cellPadding?: number[];
  /** Vertical alignment for the cell's items. */
  cellVerticalAlignment?: VerticalAlignment;
};
export type LayoutData_ResponsivenessBehaviour = 'STACK' | 'WRAP';

export const LayoutData_ResponsivenessBehaviour = {
  STACK: 'STACK',
  WRAP: 'WRAP',
} as const;

/** Used to wrap content inside layout container. When rendered, it shows content inside layout column */
export type LayoutCellNode = {
  /**
   * Node type.
   *  Must be `LAYOUT_CELL`.
   */
  type: 'LAYOUT_CELL';
  nodes: LayoutCellChildNode[];
  /** [Node ID](#node-ids). */
  id?: string;
  /** Layout Cell details. */
  layoutCellData?: LayoutCellData;
};
export type LayoutCellData = {
  /** Defines how many columns cell spans inside 12 column grid. If not provided it will divide remaining space equally. */
  colSpan?: number;
};
export type LayoutCellChildNode =
  | BlockquoteNode
  | CodeBlockNode
  | HeadingNode
  | OrderedListNode
  | BulletedListNode
  | ParagraphNode
  | AppEmbedNode
  | AudioNode
  | ButtonNode
  | CollapsibleListNode
  | DividerNode
  | EmbedNode
  | FileNode
  | GalleryNode
  | GifNode
  | HtmlNode
  | ImageNode
  | LinkPreviewNode
  | PollNode
  | VideoNode;
/** Numbered list. */
export type OrderedListNode = {
  /**
   * Node type.
   *  Must be `ORDERED_LIST`.
   */
  type: 'ORDERED_LIST';
  nodes: ListItemNode[];
  /** [Node ID](#node-ids). */
  id?: string;
  /** Ordered list details. */
  orderedListData?: OrderedListData;
};
export type OrderedListData = {
  /**
   * List margin from the left side.
   *  The indentation is measured relative to the font size *em* units by a ratio of 1.5. For example, if the indentation is `2`, then the margin is equal to **3em**. Therefore the margin is 3 times the size of the font.
   */
  indentation?: number;
  /** Additional indentation of a list node. When offset is `0`, the list node appears at its default nesting level. Higher values cause increased indentation. */
  offset?: number;
  /** Number at the top of the numbered list. <br> Default: `1` */
  start?: number;
};
/** Bulleted list. */
export type BulletedListNode = {
  /**
   * Node type.
   *  Must be `BULLETED_LIST`.
   */
  type: 'BULLETED_LIST';
  nodes: ListItemNode[];
  /** [Node ID](#node-ids). */
  id?: string;
  /** Bulleted list details. */
  bulletedListData?: BulletedListData;
};
export type BulletedListData = {
  indentation?: number;
  /** Additional indentation of a list node. When offset is `0`, the list node appears at its default nesting level. Higher values cause increased indentation. */
  offset?: number;
};
export type ListItemNode = {
  /**
   * Node type.
   *  Value: `LIST_ITEM`
   */
  type: 'LIST_ITEM';
  nodes: ListItemChildNode[];
  /** [Node ID](#node-ids). */
  id?: string;
};
export type ListItemChildNode =
  | ParagraphNode
  | HeadingNode
  | OrderedListNode
  | BulletedListNode
  | ImageNode
  | VideoNode
  | GifNode
  | GalleryNode;
/** Structures text content, providing a clear separation between different ideas or sections of text. Paragraphs are rendered with default styling that typically includes some space above and below the paragraph, making it visually distinct from the surrounding content. */
export type ParagraphNode = {
  /**
   * Node type.
   *  Must be `PARAGRAPH`.
   */
  type: 'PARAGRAPH';
  nodes?: TextNode[];
  /** [Node ID](#node-ids). */
  id?: string;
  /**
   * Vertical padding.
   *  **Note:** This node requires the [`lineSpacing`](https://dev.wix.com/docs/ricos/getting-started/introduction#plugins) plugin to be enabled.
   */
  style?: NodeStyle;
  /** Paragraph node attributes. */
  paragraphData?: ParagraphData;
};
export type ParagraphData = {
  /** Paragraph text styling. */
  textStyle?: TextStyle;
  /**
   * Paragraph margin from the left side.
   *  The indentation is measured relative to 40px. For example, if the indentation is `2`, then the margin is equal to **80px**.
   */
  indentation?: number;
  /** Paragraph level for future different paragraphs. */
  level?: number;
};
/** Text nodes hold the text itself, similar to how `span` tags hold text in HTML. Text nodes can't be nested and must contain a non-empty string. Their appearance and behavior can be modified using [decorations](#decorations). */
export type TextNode = {
  /**
   * Node type.
   *  Must be `TEXT`.
   */
  type: 'TEXT';
  /** Text options. */
  textData: TextData;
  /** [Node ID](#node-ids). */
  id?: string;
  nodes?: never[];
};
export type TextData = {
  /** Non-empty string to display as text. */
  text: string;
  decorations?: Decoration[];
};
/** Embeds an external content preview from Wix business solutions. For example, you can embed previews from the Wix Bookings, Wix Stores and Wix Events apps. */
export type AppEmbedNode = {
  /**
   * Node type.
   *  Must be `APP_EMBED`.
   */
  type: 'APP_EMBED';
  /** Embedded Wix app. */
  appEmbedData: AppEmbedData;
  /** [Node ID](#node-ids). */
  id?: string;
  nodes?: never[];
};
export type AppEmbedData = AppEmbedDataProduct | AppEmbedDataEvent | AppEmbedDataBooking;
export type AppEmbedData_AppType = 'PRODUCT' | 'EVENT' | 'BOOKING';

export const AppEmbedData_AppType = {
  PRODUCT: 'PRODUCT',
  EVENT: 'EVENT',
  BOOKING: 'BOOKING',
} as const;

/** Embedded a product from Wix Stores. */
export type AppEmbedDataProduct = {
  /** Must be `PRODUCT`. */
  type: 'PRODUCT';
  /** Embedded content name. */
  name: string;
  /** Embedded content URL. */
  url: string;
  /** App embed container styling. */
  containerData?: PluginContainerData;
  /** Embedded content thumbnail. */
  image?: Media;
  /** Embedded content ID. */
  itemId?: string;
  /** Deprecated: Use `image` instead. */
  imageSrc?: string;
  /** Whether to hide the image. */
  hideImage?: boolean;
  /** Whether to hide the title. */
  hideTitle?: boolean;
  /** Whether to hide the price. */
  hidePrice?: boolean;
  /** Whether to hide the button. */
  hideButton?: boolean;
  /** Whether to hide the ribbon. */
  hideRibbon?: boolean;
  /** Button styling options. */
  buttonStyles?: AppEmbedData_ButtonStyles;
  /** Image styling options. */
  imageStyles?: AppEmbedData_ImageStyles;
  /** Ribbon styling options. */
  ribbonStyles?: AppEmbedData_RibbonStyles;
  /** Card styling options. */
  cardStyles?: AppEmbedData_CardStyles;
  /** Pricing information for the embedded content. */
  pricingData?: AppEmbedData_PricingData;
};
/** Embeds an event from Wix Events. */
export type AppEmbedDataEvent = {
  /** Must be `EVENT`. */
  type: 'EVENT';
  /** Embedded content name. */
  name: string;
  /** Embedded content URL. */
  url: string;
  /** App embed container styling. */
  containerData?: PluginContainerData;
  /** Embedded event content. */
  eventData?: AppEmbedData_EventData;
  /** Embedded content thumbnail. */
  image?: Media;
  /** Embedded content ID. */
  itemId?: string;
  /** Deprecated: Use `image` instead. */
  imageSrc?: string;
  /** Whether to hide the image. */
  hideImage?: boolean;
  /** Whether to hide the title. */
  hideTitle?: boolean;
  /** Whether to hide the price. */
  hidePrice?: boolean;
  /** Whether to hide the description. */
  hideDescription?: boolean;
  /** Whether to hide the date and time. */
  hideDateTime?: boolean;
  /** Whether to hide the location. */
  hideLocation?: boolean;
  /** Whether to hide the button. */
  hideButton?: boolean;
  /** Whether to hide the ribbon. */
  hideRibbon?: boolean;
  /** Button styling options. */
  buttonStyles?: AppEmbedData_ButtonStyles;
  /** Image styling options. */
  imageStyles?: AppEmbedData_ImageStyles;
  /** Ribbon styling options. */
  ribbonStyles?: AppEmbedData_RibbonStyles;
  /** Card styling options. */
  cardStyles?: AppEmbedData_CardStyles;
  /** Pricing information for the embedded content. */
  pricingData?: AppEmbedData_PricingData;
};
/** Embeds a booking from Wix Bookings. */
export type AppEmbedDataBooking = {
  type: 'BOOKING';
  /** Embedded content name. */
  name: string;
  /** Embedded content URL. */
  url: string;
  /** App embed container styling. */
  containerData?: PluginContainerData;
  /** Embedded content thumbnail. */
  image?: Media;
  /** Embedded content ID. */
  itemId?: string;
  /** Embedded booking content. */
  bookingData?: AppEmbedData_BookingData;
  /** Deprecated: Use `image` instead. */
  imageSrc?: string;
  /** Whether to hide the image. */
  hideImage?: boolean;
  /** Whether to hide the title. */
  hideTitle?: boolean;
  /** Whether to hide the price. */
  hidePrice?: boolean;
  /** Whether to hide the description. */
  hideDescription?: boolean;
  /** Whether to hide the duration. */
  hideDuration?: boolean;
  /** Whether to hide the location. */
  hideLocation?: boolean;
  /** Whether to hide the button. */
  hideButton?: boolean;
  /** Whether to hide the ribbon. */
  hideRibbon?: boolean;
  /** Button styling options. */
  buttonStyles?: AppEmbedData_ButtonStyles;
  /** Image styling options. */
  imageStyles?: AppEmbedData_ImageStyles;
  /** Ribbon styling options. */
  ribbonStyles?: AppEmbedData_RibbonStyles;
  /** Card styling options. */
  cardStyles?: AppEmbedData_CardStyles;
  /** Pricing information for the embedded content. */
  pricingData?: AppEmbedData_PricingData;
};
export type AppEmbedData_BookingData = {
  /** Booking duration in minutes. */
  durations?: string;
};
export type AppEmbedData_EventData = {
  /** Event schedule. */
  scheduling?: string;
  /** Event location. */
  location?: string;
};
export type AppEmbedData_ButtonStyles = {
  /** Text to display on the button. */
  buttonText?: string;
  /** Border width in pixels. */
  borderWidth?: number;
  /** Border radius in pixels. */
  borderRadius?: number;
  /** Border color as a hexadecimal value. */
  borderColor?: string;
  /** Text color as a hexadecimal value. */
  textColor?: string;
  /** Background color as a hexadecimal value. */
  backgroundColor?: string;
  /** Border color as a hexadecimal value (hover state). */
  borderColorHover?: string;
  /** Text color as a hexadecimal value (hover state). */
  textColorHover?: string;
  /** Background color as a hexadecimal value (hover state). */
  backgroundColorHover?: string;
  /** Button size option, one of `SMALL`, `MEDIUM` or `LARGE`. Defaults to `MEDIUM`. */
  buttonSize?: string;
};
export type AppEmbedData_ImageStyles = {
  /** Position of image. Defaults to `START`. */
  imagePosition?: AppEmbedData_ImageStyles_Position;
  /** Aspect ratio for the image. Defaults to `SQUARE`. */
  aspectRatio?: AppEmbedData_ImageStyles_AspectRatio;
  /** How the image should be resized. Defaults to `FILL`. */
  resizing?: AppEmbedData_ImageStyles_Resizing;
  /** Image border color as a hexadecimal value. */
  borderColor?: string;
  /** Image border width in pixels. */
  borderWidth?: number;
  /** Image border radius in pixels. */
  borderRadius?: number;
};
export type AppEmbedData_ImageStyles_Position = 'START' | 'END' | 'TOP';

export const AppEmbedData_ImageStyles_Position = {
  START: 'START',
  END: 'END',
  TOP: 'TOP',
} as const;

export type AppEmbedData_ImageStyles_AspectRatio = 'SQUARE' | 'RECTANGLE';

export const AppEmbedData_ImageStyles_AspectRatio = {
  SQUARE: 'SQUARE',
  RECTANGLE: 'RECTANGLE',
} as const;

export type AppEmbedData_ImageStyles_Resizing = 'FILL' | 'FIT';

export const AppEmbedData_ImageStyles_Resizing = {
  FILL: 'FILL',
  FIT: 'FIT',
} as const;

export type AppEmbedData_RibbonStyles = {
  /** Text to display on the ribbon. */
  ribbonText?: string;
  /** Placement of the ribbon. Defaults to `IMAGE`. */
  ribbonPlacement?: AppEmbedData_RibbonStyles_Placement;
  /** Ribbon background color as a hexadecimal value. */
  backgroundColor?: string;
  /** Ribbon text color as a hexadecimal value. */
  textColor?: string;
  /** Ribbon border color as a hexadecimal value. */
  borderColor?: string;
  /** Ribbon border width in pixels. */
  borderWidth?: number;
  /** Ribbon border radius in pixels. */
  borderRadius?: number;
};
export type AppEmbedData_RibbonStyles_Placement = 'IMAGE' | 'PRODUCT_INFO';

export const AppEmbedData_RibbonStyles_Placement = {
  IMAGE: 'IMAGE',
  PRODUCT_INFO: 'PRODUCT_INFO',
} as const;

export type AppEmbedData_CardStyles = {
  /** Card background color as a hexadecimal value. */
  backgroundColor?: string;
  /** Card border color as a hexadecimal value. */
  borderColor?: string;
  /** Card border width in pixels. */
  borderWidth?: number;
  /** Card border radius in pixels. */
  borderRadius?: number;
  /** Card type. Defaults to `CONTAINED`. */
  type?: AppEmbedData_CardStyles_Type;
  /** Content alignment. Defaults to `START`. */
  alignment?: AppEmbedData_CardStyles_Alignment;
  /** Layout for title and price. Defaults to `STACKED`. */
  titlePriceLayout?: AppEmbedData_CardStyles_Layout;
  /** Title text color as a hexadecimal value. */
  titleColor?: string;
  /** Text color as a hexadecimal value. */
  textColor?: string;
};
export type AppEmbedData_CardStyles_Type = 'CONTAINED' | 'FRAMELESS';

export const AppEmbedData_CardStyles_Type = {
  CONTAINED: 'CONTAINED',
  FRAMELESS: 'FRAMELESS',
} as const;

export type AppEmbedData_CardStyles_Alignment = 'START' | 'CENTER' | 'END';

export const AppEmbedData_CardStyles_Alignment = {
  START: 'START',
  CENTER: 'CENTER',
  END: 'END',
} as const;

/** Layout options for arranging elements. */
export type AppEmbedData_CardStyles_Layout = 'STACKED' | 'SIDE_BY_SIDE';
export type AppEmbedData_PricingData = {
  /** Minimum numeric price value as string (e.g., "10.99"). */
  valueFrom?: string;
  /** Maximum numeric price value as string (e.g., "19.99"). */
  valueTo?: string;
  /** Numeric price value as string after discount application (e.g., "15.99"). */
  discountedValue?: string;
  /** Currency of the value in ISO 4217 format (e.g., "USD", "EUR"). */
  currency?: string;
  /** Pricing plan ID. */
  pricingPlanId?: string;
};
/** Embeds and plays audio content directly in a webpage. Users can interact with the controls to play, pause, rewind, and adjust the volume of the audio. */
export type AudioNode = {
  /**
   * Node type.
   *  Must be `AUDIO`.
   */
  type: 'AUDIO';
  /** Audio details. */
  audioData: AudioData;
  /** [Node ID](#node-ids). */
  id?: string;
  nodes?: never[];
};
export type AudioData = {
  /** Audio node container styling. */
  containerData?: PluginContainerData;
  /** Audio file details. */
  audio?: Media;
  /**
   * Whether the audio download button is disabled.
   *  Default: `false`
   */
  disableDownload?: boolean;
  /** Cover image. */
  coverImage?: Media;
  /** Track name. */
  name?: string;
  /** Author name. */
  authorName?: string;
  /** HTML version of the audio node. */
  html?: string;
};
/** Button to trigger an action. */
export type ButtonNode = {
  /**
   * Node type.
   *  Must be `BUTTON`
   */
  type: 'BUTTON';
  /** Button details. */
  buttonData: ButtonData;
  /** [Node ID](#node-ids). */
  id?: string;
  nodes?: never[];
};
export type ButtonData = {
  /** Button type. Supported values: <br> - `LINK`: Redirects to a webpage. <br> - `ACTION`: Performs an action. */
  type: ButtonData_Type;
  /** Button text. */
  text: string;
  /** Button container styling. */
  containerData?: PluginContainerData;
  /** Button styling. */
  styles?: ButtonData_Styles;
  /** Button link details. */
  link?: Link;
};
export type ButtonData_Type = 'LINK' | 'ACTION';

export const ButtonData_Type = {
  LINK: 'LINK',
  ACTION: 'ACTION',
} as const;

export type ButtonData_Styles = {
  /** Deprecated: Use `borderWidth` and `borderRadius` instead. */
  border?: ButtonData_Styles_Border;
  /** Deprecated: Use `textColor`, `borderColor` and `backgroundColor` instead. */
  colors?: ButtonData_Styles_Colors;
  /** Border width in pixels. */
  borderWidth?: number;
  /** Deprecated: Use `borderWidth` for normal/hover states instead. */
  borderWidthHover?: number;
  /** Border radius in pixels. */
  borderRadius?: number;
  /** Border color as a hexadecimal value. */
  borderColor?: string;
  /** Border color as a hexadecimal value (hover state). */
  borderColorHover?: string;
  /** Text color as a hexadecimal value. */
  textColor?: string;
  /** Text color as a hexadecimal value (hover state). */
  textColorHover?: string;
  /** Background color as a hexadecimal value. */
  backgroundColor?: string;
  /** Background color as a hexadecimal value (hover state). */
  backgroundColorHover?: string;
  /** Button size. Defaults to `MEDIUM`. */
  buttonSize?: ButtonData_Styles_ButtonSize;
};
export type ButtonData_Styles_ButtonSize = 'MEDIUM' | 'SMALL' | 'LARGE';

export const ButtonData_Styles_ButtonSize = {
  MEDIUM: 'MEDIUM',
  SMALL: 'SMALL',
  LARGE: 'LARGE',
} as const;

export type ButtonData_Styles_Colors = {
  /** Deprecated: Use `textColor` in `styles` instead. */
  text?: string;
  /** Deprecated: Use `borderColor` in `styles` instead. */
  border?: string;
  /** Deprecated: Use `backgroundColor` in `styles` instead. */
  background?: string;
};
export type ButtonData_Styles_Border = {
  /** Deprecated: Use `borderWidth` in `styles` instead. */
  width?: number;
  /** Deprecated: Use `borderRadius` in `styles` instead */
  radius?: number;
};
/** List of items where each item can be expanded or collapsed. */
export type CollapsibleListNode = {
  /**
   * Node type.
   *  Must be `COLLAPSIBLE_LIST`
   */
  type: 'COLLAPSIBLE_LIST';
  nodes: CollapsibleItemNode[];
  /** [Node ID](#node-ids). */
  id?: string;
  /** Collapsible list details. */
  collapsibleListData?: CollapsibleListData;
};
export type CollapsibleListData = {
  /** Collapsible list container styling. */
  containerData?: PluginContainerData;
  /**
   * Whether only one item can be expanded at a time.
   *  Default: `false`.
   */
  expandOnlyOne?: boolean;
  /** Sets which items are automatically expanded after the page loads. */
  initialExpandedItems?: 'FIRST' | 'ALL' | 'NONE';
  /**
   * Direction of the text in the list. Supported values:
   *  - `LTR`: Left to right.
   *  - `RTL`: Right to left.
   */
  direction?: CollapsibleListData_Direction;
  /** Whether the collapsible item will appear in search results as an FAQ. */
  isQapageData?: boolean;
};
export type CollapsibleListData_Direction = 'LTR' | 'RTL';

export const CollapsibleListData_Direction = {
  LTR: 'LTR',
  RTL: 'RTL',
} as const;

export type CollapsibleItemNode = {
  /**
   * Node type.
   *  Must be `COLLAPSIBLE_ITEM`
   */
  type: 'COLLAPSIBLE_ITEM';
  nodes: CollapsibleItemChildNode[];
  /** [Node ID](#node-ids). */
  id?: string;
};
export type CollapsibleItemChildNode = CollapsibleItemTitleNode | CollapsibleItemBodyNode;
/** Collapsible list item title. */
export type CollapsibleItemTitleNode = {
  /**
   * Node type.
   *  Must be `COLLAPSIBLE_ITEM_TITLE`.
   */
  type: 'COLLAPSIBLE_ITEM_TITLE';
  nodes: CollapsibleTitleChildNode[];
  /** [Node ID](#node-ids). */
  id?: string;
};
export type CollapsibleTitleChildNode =
  | ParagraphNode
  | HeadingNode
  | BulletedListNode
  | OrderedListNode
  | BlockquoteNode
  | CodeBlockNode;
/** Collapsible list item body. */
export type CollapsibleItemBodyNode = {
  /**
   * Node type.
   *  Must be `COLLAPSIBLE_ITEM_BODY`.
   */
  type: 'COLLAPSIBLE_ITEM_BODY';
  nodes: CollapsibleItemBodyChildNode[];
  /** [Node ID](#node-ids). */
  id?: string;
};
/** Collapsible list item body. */
export type CollapsibleItemBodyChildNode =
  | ParagraphNode
  | HeadingNode
  | BulletedListNode
  | OrderedListNode
  | BlockquoteNode
  | AudioNode
  | FileNode
  | ImageNode
  | VideoNode
  | GifNode
  | DividerNode
  | CodeBlockNode
  | AppEmbedNode
  | LinkPreviewNode
  | GalleryNode
  | ButtonNode
  | CollapsibleListNode
  | TableNode
  | EmbedNode
  | PollNode
  | HtmlNode;
/** Horizontal line between different content. */
export type DividerNode = {
  /**
   * Node type.
   *  Must be `DIVIDER`.
   */
  type: 'DIVIDER';
  /** [Node ID](#node-ids). */
  id?: string;
  /** Divider details. */
  dividerData?: DividerData;
  nodes?: never[];
};
export type DividerData = {
  /** Divider container styling. */
  containerData?: PluginContainerData;
  /** Divider line style. */
  lineStyle?: DividerData_LineStyle;
  /** Divider width. */
  width?: DividerData_Width;
  /** Divider alignment. */
  alignment?: DividerData_Alignment;
};
export type DividerData_LineStyle = 'SINGLE' | 'DOUBLE' | 'DASHED' | 'DOTTED';

export const DividerData_LineStyle = {
  SINGLE: 'SINGLE',
  DOUBLE: 'DOUBLE',
  DASHED: 'DASHED',
  DOTTED: 'DOTTED',
} as const;

export type DividerData_Width = 'LARGE' | 'MEDIUM' | 'SMALL';

export const DividerData_Width = {
  LARGE: 'LARGE',
  MEDIUM: 'MEDIUM',
  SMALL: 'SMALL',
} as const;

export type DividerData_Alignment = 'CENTER' | 'LEFT' | 'RIGHT';

export const DividerData_Alignment = {
  CENTER: 'CENTER',
  LEFT: 'LEFT',
  RIGHT: 'RIGHT',
} as const;

/** Embedded content when you include a link to a resource, without having to parse the resource directly. This provides a standardized way for web developers to include content such as videos, images, and other multimedia elements from supported providers. To learn more, see [oembed](https://oembed.com/). */
export type EmbedNode = {
  /**
   * Node type.
   *  Must be `EMBED`.
   */
  type: 'EMBED';
  /** oEmbed details. */
  embedData: EmbedData;
  /** [Node ID](#node-ids). */
  id?: string;
  nodes?: never[];
};
export type EmbedData = {
  /** oEmbed node container styling. */
  containerData?: PluginContainerData;
  /** [oEmbed](https://www.oembed.com) details. */
  oembed?: Oembed;
  /** Original asset source. */
  src?: string;
};
export type Oembed = {
  /** Resource type. */
  type?: string;
  /** Width of the resource specified in the `url` field in pixels. */
  width?: number;
  /** Height of the resource specified in the `url` field in pixels. */
  height?: number;
  /** Resource title. */
  title?: string;
  /** Resource source URL. */
  url?: string;
  /** HTML to embed a video player. The HTML should have no padding or margins. */
  html?: string;
  /** Name of the author or owner of the resource. */
  authorName?: string;
  /** URL for the author or owner of the resource. */
  authorUrl?: string;
  /** Name of the resource provider. */
  providerName?: string;
  /** URL for the resource provider. */
  providerUrl?: string;
  /** Thumbnail image URL for the resource. If defined, `thumbnailWidth` and `thumbnailHeight` must also be defined. */
  thumbnailUrl?: string;
  /** Resource's thumbnail image width. If defined, `thumbnailUrl` and `thumbnailHeight` must also be defined. */
  thumbnailWidth?: string;
  /** Resource's thumbnail image height. If defined, `thumbnailUrl` and `thumbnailWidth`must also be defined. */
  thumbnailHeight?: string;
  /** Embedded video URL. */
  videoUrl?: string;
  /** The oEmbed version number. Must be `1.0`. */
  version?: string;
};
export type ExternalNode = {
  type: 'EXTERNAL';
  id?: string;
  htmlData?: any;
  externalData?: Record<string, any>;
  nodes?: never[];
};
/** File that is uploaded and accessed in a page. Files can be different types of content, such as text documents, images, and videos. */
export type FileNode = {
  /**
   * Node type.
   *  Must be `FILE`.
   */
  type: 'FILE';
  /** File details. */
  fileData: FileData;
  /** [Node ID](#node-ids). */
  id?: string;
  nodes?: never[];
};
export type FileData = {
  /** File container styling. */
  containerData?: PluginContainerData;
  /** File data source. */
  src?: FileSource;
  /** File name. */
  name?: string;
  /** File type. */
  type?: string;
  /** Deprecated. Replaced by `sizeInKb` */
  size?: number;
  /** File size in KB. */
  sizeInKb?: string;
  /** Settings for PDF files. */
  pdfSettings?: FileData_PDFSettings;
  /** File MIME type. */
  mimeType?: string;
  /** File path. */
  path?: string;
};
export type FileData_PDFSettings = {
  /**
   * PDF view mode. Supported values:
   *  - `NONE`: PDF isn't displayed.
   *  - `FULL`: Full PDF page view is displayed.
   *  - `MINI`: Mini view of the PDF is displayed.
   */
  viewMode?: FileData_PDFSettings_ViewMode;
  /**
   * Whether the PDF download button is disabled.
   *  Default: `false`
   */
  disableDownload?: boolean;
  /**
   * Whether the PDF print button is disabled.
   *  Default: `false`
   */
  disablePrint?: boolean;
};
export type FileData_PDFSettings_ViewMode = 'NONE' | 'FULL' | 'MINI';

export const FileData_PDFSettings_ViewMode = {
  NONE: 'NONE',
  FULL: 'FULL',
  MINI: 'MINI',
} as const;

/** Collection of images displayed together. */
export type GalleryNode = {
  /**
   * Node type.
   *  Must be `GALLERY`.
   */
  type: 'GALLERY';
  /** Gallery node details. */
  galleryData: GalleryData;
  /** [Node ID](#node-ids). */
  id?: string;
  nodes?: never[];
};
export type GalleryData = {
  items: GalleryData_Item[];
  /** Gallery container styling. */
  containerData?: PluginContainerData;
  /** Gallery appearance options. */
  options?: GalleryOptions;
  /**
   * Whether the expand button is disabled.
   * Default: `false`
   */
  disableExpand?: boolean;
  /**
   * Whether the download button is disabled.
   * Default: `false`
   */
  disableDownload?: boolean;
};
export type GalleryOptions = {
  /** Gallery layout. */
  layout?: GalleryOptions_Layout;
  /** Styling for gallery items. */
  item?: GalleryOptions_ItemStyle;
  /** Styling for gallery thumbnail images. */
  thumbnails?: GalleryOptions_Thumbnails;
};
export type GalleryOptions_Layout = {
  /** Gallery layout. Supported values: <br> - `COLLAGE`: Presents images in various sizes and positions, creating an artistic or dynamic layout. <br> - `MASONRY`: Grid-based design that places items in optimal positions based on available vertical space, creating an arrangement where items of varying heights fit together like bricks in a wall. <br> - `GRID`: A layout structure that organizes items into rows and columns, creating a uniform arrangement of images. <br> - `THUMBNAIL`: Smaller, scaled-down version of an image that serves as a preview. <br> - `SLIDER`: Component that allows users to browse through a set of images by sliding or swiping through them, typically in a confined viewing area. <br> - `SLIDESHOW`: A component that automatically or manually transitions through a set of images in a designated area. <br> - `PANORAMA`: Wide-angle or 360-degree image that allows users to view an extended horizontal view of a scene. <br> - `COLUMN`: Arrangement of the gallery items in vertical sections. <br> - `MAGIC`: Visually stunning, dynamic effects that enhance the user experience. <br> - `FULLSIZE`: A layout where each image or item is displayed at its full size, sometimes  taking up the entire viewport or a significant portion of the screen. */
  type?: GalleryOptions_Layout_Type;
  /**
   * Whether the horizontal scroll is enabled.
   * Default: `true`, unless the `type` field is set to `GRID` or `COLLAGE`.
   */
  horizontalScroll?: boolean;
  /** Gallery orientation. */
  orientation?: GalleryOptions_Layout_Orientation;
  /** Number of columns to display on computer screens. */
  numberOfColumns?: number;
  /** Number of columns to display on mobile phone screens. */
  mobileNumberOfColumns?: number;
};
export type GalleryOptions_Layout_Type =
  | 'COLLAGE'
  | 'MASONRY'
  | 'GRID'
  | 'THUMBNAIL'
  | 'SLIDER'
  | 'SLIDESHOW'
  | 'PANORAMA'
  | 'COLUMN'
  | 'MAGIC'
  | 'FULLSIZE';

export const GalleryOptions_Layout_Type = {
  COLLAGE: 'COLLAGE',
  MASONRY: 'MASONRY',
  GRID: 'GRID',
  THUMBNAIL: 'THUMBNAIL',
  SLIDER: 'SLIDER',
  SLIDESHOW: 'SLIDESHOW',
  PANORAMA: 'PANORAMA',
  COLUMN: 'COLUMN',
  MAGIC: 'MAGIC',
  FULLSIZE: 'FULLSIZE',
} as const;

export type GalleryOptions_Layout_Orientation = 'ROWS' | 'COLUMNS';

export const GalleryOptions_Layout_Orientation = {
  ROWS: 'ROWS',
  COLUMNS: 'COLUMNS',
} as const;

export type GalleryOptions_ItemStyle = {
  /** Dimensions for each gallery item in pixels. Behavior may change with gallery type. */
  targetSize?: number;
  /** Height to width ratio. */
  ratio?: number;
  /** Cropping strategy. Supported values: <br> - `FILL`: Each item in the gallery is cropped and resized to completely fill its container. <br> - `FIT`: Each item in the gallery is resized to fit exactly in its container without cropping. */
  crop?: GalleryOptions_ItemStyle_Crop;
  /** Spacing between gallery items in pixels. */
  spacing?: number;
};
export type GalleryOptions_ItemStyle_Crop = 'FILL' | 'FIT';

export const GalleryOptions_ItemStyle_Crop = {
  FILL: 'FILL',
  FIT: 'FIT',
} as const;

export type GalleryOptions_Thumbnails = {
  /** Thumbnail alignment. */
  placement?: GalleryOptions_Thumbnails_Alignment;
  /** Spacing between thumbnails in pixels. */
  spacing?: number;
};
export type GalleryOptions_Thumbnails_Alignment = 'TOP' | 'RIGHT' | 'BOTTOM' | 'LEFT' | 'NONE';

export const GalleryOptions_Thumbnails_Alignment = {
  TOP: 'TOP',
  RIGHT: 'RIGHT',
  BOTTOM: 'BOTTOM',
  LEFT: 'LEFT',
  NONE: 'NONE',
} as const;

export type GalleryData_Item = {
  /** Item title. */
  title?: string;
  /** Item's alternative text. */
  altText?: string;
  /** Image item. */
  image?: GalleryData_Item_Image;
  /** Video item. */
  video?: GalleryData_Item_Video;
};
export type GalleryData_Item_Video = {
  /** Video file details. */
  media: Media;
  /** Video thumbnail file details. */
  thumbnail?: Media;
};
export type GalleryData_Item_Image = {
  /** Image file details. */
  media: Media;
  /** Link details for images, if relevant. */
  link?: Link;
};
/** GIF images. */
export type GifNode = {
  /**
   * Node type.
   *  Must be `GIF`.
   */
  type: 'GIF';
  /** GIF details. */
  gifData: GIFData;
  /** [Node ID](#node-ids). */
  id?: string;
  nodes?: CaptionNode[];
};
export type GIFData = {
  /** GIF container styling. */
  containerData?: PluginContainerData;
  /** Source for the full size GIF. */
  original?: GIF;
  /** Source for the downsized GIF. */
  downsized?: GIF;
  /** Height in pixels. */
  height?: number;
  /** Width in pixels. */
  width?: number;
  /**
   * Type of GIF (Sticker or NORMAL).
   *  Default: `NORMAL`
   */
  gifType?: GIFData_GIFType;
};
export type GIFData_GIFType = 'NORMAL' | 'STICKER';

export const GIFData_GIFType = {
  NORMAL: 'NORMAL',
  STICKER: 'STICKER',
} as const;

export type GIF = {
  /** GIF source URL. */
  gif?: string;
  /** MP4 source URL. */
  mp4?: string;
  /** GIF thumbnail URL. */
  still?: string;
};
/** HTML block that incorporates external content, such as interactive maps or entire web pages. */
export type HtmlNode = {
  /**
   * Node type.
   *  Mst be `HTML`.
   */
  type: 'HTML';
  /** HTML details. */
  htmlData: HTMLData;
  /** [Node ID](#node-ids). */
  id?: string;
  nodes?: never[];
};
export type HTMLData = {
  /** HTML node container styling. Height property is irrelevant for HTML embeds when autoHeight is set to `true`. */
  containerData?: PluginContainerData;
  /**
   * HTML code type. Possible values:
   *  - `HTML`: HTML code.
   *  - [`ADSENSE`](https://adsense.google.com/start/): Service provided by Google for rendering advertisements on web pages.
   *  - `AI`: AI-generated HTML code.
   */
  source?: HTMLData_Source;
  /** HTML code URL. */
  url?: string;
  /** HTML code. */
  html?: string;
  /**
   * If container height is aligned with its content height.
   *  Default: `true`
   */
  autoHeight?: boolean;
};
export type HTMLData_Source = 'HTML' | 'ADSENSE' | 'AI';

export const HTMLData_Source = {
  HTML: 'HTML',
  ADSENSE: 'ADSENSE',
  AI: 'AI',
} as const;

/** Standalone images in a page. */
export type ImageNode = {
  /**
   * Node type.
   *  Must be `IMAGE`.
   */
  type: 'IMAGE';
  /** Image options. */
  imageData: ImageData;
  /** [Node ID](#node-ids). */
  id?: string;
  nodes?: CaptionNode[];
};
export type ImageData = {
  /** Image file details. */
  image: Media;
  /** Image container styling. */
  containerData?: PluginContainerData;
  /** Image link details. */
  link?: Link;
  /**
   * Whether the image expands to the full screen when clicked.
   *  Default: `false`
   */
  disableExpand?: boolean;
  /** Alternate text to display when the image doesn't load. */
  altText?: string;
  /** Deprecated. Use `CaptionNode` instead. */
  caption?: string;
  /**
   * Whether the download image button is disabled.
   *  Default: `false`
   */
  disableDownload?: boolean;
  /** Whether the image is decorative and does not need an explanation. Defaults to `false`. */
  decorative?: boolean;
  /** Image styling. */
  styles?: ImageData_Styles;
};
export type ImageData_Styles = {
  /** Border properties. */
  border?: ImageData_Styles_Border;
};
export type ImageData_Styles_Border = {
  /** Border width in pixels. */
  width?: number;
  /** Border color as a hexadecimal value. */
  color?: string;
  /** Border radius in pixels. */
  radius?: number;
};
/** Preview or summary of a webpage. Link previews include elements such as the title of the linked page, a brief description or excerpt of the content, and sometimes a thumbnail image or other relevant information. */
export type LinkPreviewNode = {
  /**
   * Node type.
   *  Must be `LINK_PREVIEW`.
   */
  type: 'LINK_PREVIEW';
  /** Link preview details. */
  linkPreviewData: LinkPreviewData;
  /** [Node ID](#node-ids). */
  id?: string;
  nodes?: never[];
};
export type LinkPreviewData = {
  /** Link preview container styling. */
  containerData?: PluginContainerData;
  /** Link details. */
  link?: Link;
  /** Preview title. */
  title?: string;
  /** Preview thumbnail URL. */
  thumbnailUrl?: string;
  /** Preview description. */
  description?: string;
  /** Preview content as HTML. */
  html?: string;
  /** Styling for the link preview. */
  styles?: LinkPreviewData_Styles;
};
export type LinkPreviewData_Styles = {
  /** Background color as a hexadecimal value. */
  backgroundColor?: string;
  /** Title color as a hexadecimal value. */
  titleColor?: string;
  /** Subtitle color as a hexadecimal value. */
  subtitleColor?: string;
  /** Link color as a hexadecimal value. */
  linkColor?: string;
  /** Border width in pixels. */
  borderWidth?: number;
  /** Border radius in pixels. */
  borderRadius?: number;
  /** Border color as a hexadecimal value. */
  borderColor?: string;
  /** Position of thumbnail. Defaults to `START`. */
  thumbnailPosition?: 'START' | 'END' | 'TOP' | 'HIDDEN';
};
/** Provides a question with several options, where the options' cumulative total may be shown. */
export type PollNode = {
  /**
   * Node type.
   *  Must be `POLL`.
   */
  type: 'POLL';
  /** Poll details. */
  pollData: PollData;
  /** [Node ID](#node-ids). */
  id?: string;
  nodes?: never[];
};
export type PollData = {
  /** Poll container styling. */
  containerData?: PluginContainerData;
  /** Poll data. */
  poll?: PollData_Poll;
  /** Poll layout settings. */
  layout?: PollData_Layout;
  /** Poll design. */
  design?: PollData_Design;
};
export type PollData_Poll = {
  /** [Node ID](#node-ids). */
  id?: string;
  /** Poll title. */
  title?: string;
  /** Poll creator ID. */
  creatorId?: string;
  /** Main poll image. */
  image?: Media;
  options?: PollData_Poll_Option[];
  /** Poll display settings. */
  settings?: PollData_Poll_Settings;
};
export type PollData_Poll_Option = {
  /** Poll option ID. */
  id?: string;
  /** Poll option title */
  title?: string;
  /** Image displayed with the option. */
  image?: Media;
};
export type PollData_Poll_Settings = {
  /** Voting permission settings. */
  permissions?: PollData_Poll_Settings_Permissions;
  /**
   * Whether to display the voters in the vote results.
   *  Default: `true`.
   */
  showVoters?: boolean;
  /**
   * Whether to display the vote count.
   *  Default: `true`.
   */
  showVotesCount?: boolean;
};
export type PollData_Poll_Settings_Permissions = {
  /** Who can view the poll results. */
  view?: PollData_Poll_Settings_Permissions_ViewRole;
  /** Who can vote. */
  vote?: PollData_Poll_Settings_Permissions_VoteRole;
  /**
   * Whether a user can vote multiple times.
   *  Default: `false`
   */
  allowMultipleVotes?: boolean;
};
export type PollData_Poll_Settings_Permissions_ViewRole = 'CREATOR' | 'VOTERS' | 'EVERYONE';

export const PollData_Poll_Settings_Permissions_ViewRole = {
  CREATOR: 'CREATOR',
  VOTERS: 'VOTERS',
  EVERYONE: 'EVERYONE',
} as const;

export type PollData_Poll_Settings_Permissions_VoteRole = 'SITE_MEMBERS' | 'ALL';

export const PollData_Poll_Settings_Permissions_VoteRole = {
  SITE_MEMBERS: 'SITE_MEMBERS',
  ALL: 'ALL',
} as const;

export type PollData_Layout = {
  /** Poll layout settings. */
  poll?: PollData_Layout_PollLayout;
  /** Poll options layout settings. */
  options?: PollData_Layout_OptionLayout;
};
export type PollData_Layout_PollLayout = {
  /** Voting options layout. */
  type?: PollData_Layout_PollLayout_Type;
  /** Direction of the text in the poll. Supported values: <br> - `LTR`: Left to right. <br> - `RTL`: Right to left. */
  direction?: PollData_Layout_PollLayout_Direction;
  /**
   * Whether to display the main poll image.
   *  Default: `false`
   */
  enableImage?: boolean;
};
export type PollData_Layout_PollLayout_Type = 'LIST' | 'GRID';

export const PollData_Layout_PollLayout_Type = {
  LIST: 'LIST',
  GRID: 'GRID',
} as const;

export type PollData_Layout_PollLayout_Direction = 'LTR' | 'RTL';

export const PollData_Layout_PollLayout_Direction = {
  LTR: 'LTR',
  RTL: 'RTL',
} as const;

export type PollData_Layout_OptionLayout = {
  /**
   * Whether to display option images.
   *  Defaults: `false`.
   */
  enableImage?: boolean;
};
export type PollData_Design = {
  /** Poll styling. */
  poll?: PollData_Design_PollDesign;
  /** Voting options styling. */
  options?: PollData_Design_OptionDesign;
};
export type PollData_Design_PollDesign = {
  /** Poll background styling. */
  background?: PollData_Design_PollDesign_Background;
  /** Border radius in pixels. */
  borderRadius?: number;
};
export type PollData_Design_PollDesign_Background =
  | PollData_Background_ColorVariant
  | PollData_Background_ImageVariant
  | PollData_Background_GradientVariant;
export type PollData_Design_PollDesign_Background_Type = 'COLOR' | 'IMAGE' | 'GRADIENT';

export const PollData_Design_PollDesign_Background_Type = {
  COLOR: 'COLOR',
  IMAGE: 'IMAGE',
  GRADIENT: 'GRADIENT',
} as const;

export type PollData_Background_ColorVariant = {
  type: 'COLOR';
  /** Background color as a hexademical value. */
  color?: string;
};
export type PollData_Background_ImageVariant = {
  type: 'IMAGE';
  /** Image to use for the background. */
  image?: Media;
};
export type PollData_Background_GradientVariant = {
  type: 'GRADIENT';
  /** Gradient background details. */
  gradient?: PollData_Design_PollDesign_Background_Gradient;
};
export type PollData_Design_PollDesign_Background_Gradient = {
  /** Gradient angle in degrees. */
  angle?: number;
  /** Start color as a hexadecimal value. */
  startColor?: string;
  /** End color as a hexadecimal value. */
  lastColor?: string;
};
export type PollData_Design_OptionDesign = {
  /** Border radius in pixels. */
  borderRadius?: number;
};
/** Data in rows and columns. */
export type TableNode = {
  /**
   * Node type.
   *  Must be `TABLE`.
   */
  type: 'TABLE';
  nodes: TableRowNode[];
  /** [Node ID](#node-ids). */
  id?: string;
  /** Table details. */
  tableData?: TableData;
};
export type TableRowNode = {
  /**
   * Node type.
   *  Must be `TABLE_ROW`.
   */
  type: 'TABLE_ROW';
  nodes: TableCellNode[];
  /** [Node ID](#node-ids). */
  id?: string;
};
export type TableData = {
  /** Table container styling. */
  containerData?: PluginContainerData;
  /** Table dimensions. */
  dimensions?: TableData_Dimensions;
  /** Whether the table's first row is a heading. <br> Default: `false` */
  rowHeader?: boolean;
  /** Whether the table's first column is a heading. <br> Default: `false` */
  columnHeader?: boolean;
};
export type TableData_Dimensions = {
  colsWidthRatio?: number[];
  rowsHeight?: number[];
  colsMinWidth?: number[];
};
export type TableCellNode = {
  /**
   * Node type.
   *  Must be `TABLE_CELL`.
   */
  type: 'TABLE_CELL';
  nodes: TableCellChildNode[];
  /** [Node ID](#node-ids). */
  id?: string;
  /** Table cell details. */
  tableCellData?: TableCellData;
};
/** Table cell content node. */
export type TableCellChildNode =
  | ParagraphNode
  | HeadingNode
  | BulletedListNode
  | OrderedListNode
  | BlockquoteNode
  | AudioNode
  | FileNode
  | ImageNode
  | VideoNode
  | GifNode
  | DividerNode
  | CodeBlockNode
  | HtmlNode
  | LinkPreviewNode
  | AppEmbedNode
  | ButtonNode
  | EmbedNode;
export type TableCellData = {
  /** Cell styling. */
  cellStyle?: TableCellData_CellStyle;
  /** Cell border colors. */
  borderColors?: TableCellData_BorderColors;
};
export type TableCellData_CellStyle = {
  /** Vertical alignment for the cell's text. */
  verticalAlignment?: VerticalAlignment;
  /** Cell background color as a hexadecimal value. */
  backgroundColor?: string;
};
export type TableCellData_BorderColors = {
  /** Left border color as a hexadecimal value. */
  left?: string;
  /** Right border color as a hexadecimal value. */
  right?: string;
  /** Top border color as a hexadecimal value. */
  top?: string;
  /** Bottom border color as a hexadecimal value. */
  bottom?: string;
};
/** Embeds and plays video content directly in a page. When rendered, it is displayed as the video player with the specified video file. Users can interact with the controls to play, pause, rewind, and adjust the volume of the video. */
export type VideoNode = {
  /**
   * Node type.
   *  Must be `VIDEO`.
   */
  type: 'VIDEO';
  /** Video details. */
  videoData: VideoData;
  /** [Node ID](#node-ids). */
  id?: string;
  nodes?: CaptionNode[];
};
export type VideoData = {
  /** Video file details. */
  video: Media;
  /** Video container styling. */
  containerData?: PluginContainerData;
  /** Video thumbnail details. */
  thumbnail?: Media;
  /**
   * Whether the video download button is disabled.
   *  Default: `false`
   */
  disableDownload?: boolean;
  /** Video title. */
  title?: string;
  /** Playback details. */
  options?: PlaybackOptions;
};
export type PlaybackOptions = {
  /** Whether the media automatically starts playing after opening a page. */
  autoPlay?: boolean;
  /** Whether the media is looped. */
  playInLoop?: boolean;
  /** Whether to show the media controls. */
  showControls?: boolean;
};
/** Nodes that can be added to the root level of `nodes` array. */
export type RootNode =
  | ParagraphNode
  | HeadingNode
  | BulletedListNode
  | OrderedListNode
  | BlockquoteNode
  | CodeBlockNode
  | VideoNode
  | DividerNode
  | FileNode
  | GalleryNode
  | GifNode
  | HtmlNode
  | ImageNode
  | LinkPreviewNode
  | PollNode
  | AppEmbedNode
  | ButtonNode
  | CollapsibleListNode
  | TableNode
  | EmbedNode
  | AudioNode
  | LayoutNode
  | ExternalNode;

export const LATEST_VERSION = 1;
export type RicosDocumentNode =
  | ParagraphNode
  | TextNode
  | HeadingNode
  | BulletedListNode
  | OrderedListNode
  | ListItemNode
  | BlockquoteNode
  | CodeBlockNode
  | VideoNode
  | DividerNode
  | FileNode
  | GalleryNode
  | GifNode
  | HtmlNode
  | ImageNode
  | LinkPreviewNode
  | PollNode
  | AppEmbedNode
  | ButtonNode
  | CaptionNode
  | CollapsibleListNode
  | CollapsibleItemNode
  | CollapsibleItemTitleNode
  | CollapsibleItemBodyNode
  | TableNode
  | EmbedNode
  | TableCellNode
  | TableRowNode
  | AudioNode
  | LayoutNode
  | LayoutCellNode
  | ExternalNode;

export const RicosDocumentNodeType = {
  PARAGRAPH: 'PARAGRAPH',
  TEXT: 'TEXT',
  HEADING: 'HEADING',
  BULLETED_LIST: 'BULLETED_LIST',
  ORDERED_LIST: 'ORDERED_LIST',
  LIST_ITEM: 'LIST_ITEM',
  BLOCKQUOTE: 'BLOCKQUOTE',
  CODE_BLOCK: 'CODE_BLOCK',
  VIDEO: 'VIDEO',
  DIVIDER: 'DIVIDER',
  FILE: 'FILE',
  GALLERY: 'GALLERY',
  GIF: 'GIF',
  HTML: 'HTML',
  IMAGE: 'IMAGE',
  LINK_PREVIEW: 'LINK_PREVIEW',
  POLL: 'POLL',
  APP_EMBED: 'APP_EMBED',
  BUTTON: 'BUTTON',
  COLLAPSIBLE_LIST: 'COLLAPSIBLE_LIST',
  COLLAPSIBLE_ITEM: 'COLLAPSIBLE_ITEM',
  COLLAPSIBLE_ITEM_TITLE: 'COLLAPSIBLE_ITEM_TITLE',
  COLLAPSIBLE_ITEM_BODY: 'COLLAPSIBLE_ITEM_BODY',
  TABLE: 'TABLE',
  EMBED: 'EMBED',
  TABLE_CELL: 'TABLE_CELL',
  TABLE_ROW: 'TABLE_ROW',
  EXTERNAL: 'EXTERNAL',
  AUDIO: 'AUDIO',
  CAPTION: 'CAPTION',
  LAYOUT: 'LAYOUT',
  LAYOUT_CELL: 'LAYOUT_CELL',
  // for compat with legacy node
  UNRECOGNIZED: 'UNRECOGNIZED',
  MAP: 'MAP',
} as const;

export type RicosDocumentNodeType = keyof typeof RicosDocumentNodeType;

// For backwards compatibility

export type RichContent = RicosDocument;

export const RichContent = {
  fromJSON: (content: any): RichContent => {
    return content;
  },
  toJSON: (content: RichContent): any => {
    return content;
  },
};

export type Node_Type = RicosDocumentNodeType;
export const Node_Type = RicosDocumentNodeType;

export type Node = RicosDocumentNode;

// For places where enum value used as type
export namespace Node_Type {
  export type PARAGRAPH = 'PARAGRAPH';
  export type TEXT = 'TEXT';
  export type HEADING = 'HEADING';
  export type BULLETED_LIST = 'BULLETED_LIST';
  export type ORDERED_LIST = 'ORDERED_LIST';
  export type LIST_ITEM = 'LIST_ITEM';
  export type BLOCKQUOTE = 'BLOCKQUOTE';
  export type CODE_BLOCK = 'CODE_BLOCK';
  export type VIDEO = 'VIDEO';
  export type DIVIDER = 'DIVIDER';
  export type FILE = 'FILE';
  export type GALLERY = 'GALLERY';
  export type GIF = 'GIF';
  export type HTML = 'HTML';
  export type IMAGE = 'IMAGE';
  export type LINK_PREVIEW = 'LINK_PREVIEW';
  export type POLL = 'POLL';
  export type APP_EMBED = 'APP_EMBED';
  export type BUTTON = 'BUTTON';
  export type COLLAPSIBLE_LIST = 'COLLAPSIBLE_LIST';
  export type COLLAPSIBLE_ITEM = 'COLLAPSIBLE_ITEM';
  export type COLLAPSIBLE_ITEM_TITLE = 'COLLAPSIBLE_ITEM_TITLE';
  export type COLLAPSIBLE_ITEM_BODY = 'COLLAPSIBLE_ITEM_BODY';
  export type TABLE = 'TABLE';
  export type EMBED = 'EMBED';
  export type TABLE_CELL = 'TABLE_CELL';
  export type TABLE_ROW = 'TABLE_ROW';
  export type EXTERNAL = 'EXTERNAL';
  export type AUDIO = 'AUDIO';
  export type LAYOUT = 'LAYOUT';
  export type LAYOUT_CELL = 'LAYOUT_CELL';
}
